/**
 * Aug-2026 (User-Wunsch Jörg) — Filial-QR im Dokumenten-Postausgang.
 * Dauerhafter, filial-spezifischer QR: Handy scannt → öffentliche Upload-Seite
 * (/m/outbox/<token>). Klick = Popover mit großem QR + Druck (A4-Aushang),
 * Doppelklick = Upload-Seite direkt öffnen (Funktion starten).
 */
import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Loader2, Printer, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export default function OutboxMobileQrCard() {
    const { user } = useAuth();
    const [info, setInfo] = useState(null); // { portal_url, branch_label }

    useEffect(() => {
        api.get("/documents/outbox/mobile-qr")
            .then((r) => setInfo(r.data))
            .catch(() => setInfo(null));
    }, []);

    const url = info?.portal_url || "";

    const handlePrint = async () => {
        if (!url) return;
        const ok = await openQrPrint({
            title: "Belege per Handy hochladen",
            subtitle: `Mit der Handy-Kamera scannen — Beleg fotografieren oder PDF wählen. Die KI ordnet das Dokument automatisch Fahrzeug & Kunde zu (Standort: ${info?.branch_label || ""}).`,
            scanUrl: url,
            ...dealerPrintInfo(user),
        });
        if (!ok) toast.error("Bitte Pop-ups erlauben, um zu drucken.");
    };

    const openPortal = () => { if (url) window.open(url, "_blank"); };

    return (
        <div className="flex flex-col items-center gap-1.5 shrink-0" data-testid="outbox-mobile-qr">
            <Popover>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        onDoubleClick={openPortal}
                        title="Belege per Handy hochladen — Klick: vergrößern & drucken · Doppelklick: Upload-Seite öffnen"
                        className="bg-white rounded-md p-1.5 hover:ring-2 hover:ring-[#FFCC00] transition-shadow shadow-lg"
                        data-testid="outbox-mobile-qr-trigger"
                    >
                        {url ? (
                            <QRCodeSVG value={url} size={72} level="M" title="QR-Code: Belege per Handy hochladen" />
                        ) : (
                            <div className="w-[72px] h-[72px] flex items-center justify-center"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>
                        )}
                    </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72" data-testid="outbox-mobile-qr-popover">
                    <div className="flex flex-col items-center text-center gap-2">
                        <div className="font-display font-black text-sm flex items-center gap-1.5">
                            <Smartphone className="w-4 h-4 text-[#111827]" /> Belege per Handy hochladen
                        </div>
                        <div className="p-2 bg-white border-2 border-[#FFCC00] rounded-sm">
                            {url
                                ? <QRCodeSVG value={url} size={180} level="M" data-testid="outbox-mobile-qr-large" />
                                : <div className="w-[180px] h-[180px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
                        </div>
                        <p className="text-[11px] text-gray-600 leading-snug">
                            Mit der Handy-Kamera scannen — <b>kein Login nötig</b>. Beleg fotografieren
                            oder PDF wählen; das Dokument erscheint hier im Postausgang zur Zuordnung.
                        </p>
                        {info?.branch_label && (
                            <div className="w-full flex items-center gap-1.5 text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1" data-testid="outbox-mobile-qr-branch">
                                <MapPin className="w-3 h-3 text-emerald-600 shrink-0" />
                                <span>Belege gehen an: <b className="text-gray-700">{info.branch_label}</b></span>
                            </div>
                        )}
                        <button type="button" onClick={handlePrint} disabled={!url}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black disabled:opacity-50"
                            data-testid="outbox-mobile-qr-print">
                            <Printer className="w-4 h-4" /> Aushang drucken (A4)
                        </button>
                        <button type="button" onClick={openPortal} disabled={!url}
                            className="w-full inline-flex items-center justify-center gap-2 border border-gray-300 rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-50"
                            data-testid="outbox-mobile-qr-open">
                            <ExternalLink className="w-4 h-4" /> Upload-Seite öffnen
                        </button>
                    </div>
                </PopoverContent>
            </Popover>
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1 max-w-[110px] text-center leading-tight">
                <Smartphone className="w-3 h-3 shrink-0" /> Handy-Upload (diese Filiale)
            </span>
        </div>
    );
}
